import { isEvaluable } from "@next-core/cook";
import { isObject } from "@next-core/utils/general";
import { pick } from "lodash";
import { checkIf } from "./compute/checkIf.js";
import { computeRealValue } from "./compute/computeRealValue.js";
import { getHistory } from "../history.js";
import { getProviderBrick } from "./data/getProviderBrick.js";
import { startPoll } from "./poll.js";
import { isPreEvaluated } from "./compute/evaluate.js";
import { setProperties } from "./compute/setProperties.js";
import { applyMode, applyTheme } from "../themeAndMode.js";
import { getTplHostElement, getTplStateStore } from "./CustomTemplates/utils.js";
import { handleHttpError } from "../handleHttpError.js";
import { Notification } from "../Notification.js";
import { getFormStateStore } from "./FormRenderer/utils.js";
import { hooks } from "./Runtime.js";
import { startSSEStream } from "./sse.js";
import { debugManager } from "./debugManager.js";
export function bindListeners(brick, eventsMap, runtimeContext) {
  if (!eventsMap) {
    return;
  }
  Object.entries(eventsMap).forEach(_ref => {
    let [eventType, handlers] = _ref;
    const listener = listenerFactory(handlers, runtimeContext, {
      element: brick
    });
    brick.addEventListener(eventType, listener);

    // Remember added listeners for unbinding.
    if (!brick.$$listeners) {
      brick.$$listeners = [];
    }
    brick.$$listeners.push([eventType, listener]);

    // Remember added listeners for devtools.
    if (!brick.$$eventListeners) {
      brick.$$eventListeners = [];
    }
    for (const handler of [].concat(handlers)) {
      brick.$$eventListeners.push([eventType, null, handler]);
    }
  });
}
export function unbindListeners(brick) {
  if (brick.$$listeners) {
    for (const [eventType, listener] of brick.$$listeners) {
      brick.removeEventListener(eventType, listener);
    }
    brick.$$listeners.length = 0;
  }
}
export function isBuiltinHandler(handler) {
  return typeof handler.action === "string";
}
export function isUseProviderHandler(handler) {
  return typeof handler.useProvider === "string";
}
export function isCustomHandler(handler) {
  return !!((handler.target || handler.targetRef) && (handler.method || handler.properties));
}
export function isExecuteCustomHandler(handler) {
  return !!handler.method;
}
export function isSetPropsCustomHandler(handler) {
  return !!handler.properties;
}
export function isConditionalEventHandler(handler) {
  return !!handler.then;
}
export function listenerFactory(handlers, _runtimeContext, runtimeBrick) {
  return function (event) {
    var _handler$batch, _handler$batch2;
    for (const handler of [].concat(handlers)) {
      let runtimeContext = _runtimeContext;
      if (handler.key && typeof handler.key === "string") {
        runtimeContext = {
          ..._runtimeContext,
          eventMap: new Map([...(_runtimeContext.eventMap || []), [handler.key, event]])
        };
      }
      if (!checkIf(handler, {
        ...runtimeContext,
        event
      })) {
        if (handler.else) {
          listenerFactory(handler.else, runtimeContext, runtimeBrick)(event);
        }
        continue;
      }
      if (isConditionalEventHandler(handler)) {
        listenerFactory(handler.then, runtimeContext, runtimeBrick)(event);
      } else if (isBuiltinHandler(handler)) {
        const [object, method] = handler.action.split(".");
        switch (handler.action) {
          case "history.push":
          case "history.replace":
          case "history.pushQuery":
          case "history.replaceQuery":
          case "history.pushAnchor":
          case "history.block":
          case "history.goBack":
          case "history.goForward":
          case "history.reload":
          case "history.unblock":
            handleHistoryAction(event, method, handler.args, handler.callback, runtimeContext);
            break;

          // case "segue.push":
          // case "segue.replace":
          // case "alias.push":
          // case "alias.replace":

          case "window.open":
            handleWindowOpenAction(event, handler.args, runtimeContext);
            break;
          case "window.postMessage":
            handleWindowPostMessageAction(event, handler.args, runtimeContext);
            break;
          case "parent.postMessage":
            handleParentPostMessageAction(event, handler.args, runtimeContext);
            break;
          case "location.reload":
          case "location.assign":
            handleLocationAction(event, method, handler.args, runtimeContext);
            break;
          case "localStorage.setItem":
          case "localStorage.removeItem":
          case "sessionStorage.setItem":
          case "sessionStorage.removeItem":
            handleStorageAction(event, object, method, handler.args, runtimeContext);
            break;
          case "event.preventDefault":
          case "event.stopPropagation":
            handleEventAction(event, method, handler.args, runtimeContext);
            break;
          case "console.log":
          case "console.error":
          case "console.warn":
          case "console.info":
            handleConsoleAction(event, method, handler.args, runtimeContext);
            break;
          case "message.success":
          case "message.error":
          case "message.info":
          case "message.warn":
            handleMessageAction(event, method, handler.args, runtimeContext);
            break;
          case "handleHttpError":
            handleHttpError(event.detail);
            break;
          case "context.assign":
          case "context.replace":
          case "context.refresh":
          case "context.load":
          case "context.set":
            handleContextAction(event, method, handler.args, (_handler$batch = handler.batch) !== null && _handler$batch !== void 0 ? _handler$batch : true, handler.callback, runtimeContext);
            break;
          case "state.update":
          case "state.refresh":
          case "state.load":
          case "state.set":
            handleTplStateAction(event, method, handler.args, (_handler$batch2 = handler.batch) !== null && _handler$batch2 !== void 0 ? _handler$batch2 : true, handler.callback, runtimeContext);
            break;
          case "tpl.dispatchEvent":
            {
              const [type, init] = argsFactory(handler.args, runtimeContext, event);
              const tplHostElement = getTplHostElement(runtimeContext, handler.action, `: ${type}`);
              tplHostElement.dispatchEvent(new CustomEvent(type, init));
              break;
            }
          case "formstate.update":
            handleFormStateAction(event, handler.args, handler.callback, runtimeContext);
            break;
          case "message.subscribe":
          case "message.unsubscribe":
            handleMessageDispatcher(event, method, handler.args, runtimeContext, runtimeBrick, handler.callback);
            break;
          case "theme.setDarkTheme":
          case "theme.setLightTheme":
            applyTheme(handler.action === "theme.setDarkTheme" ? "dark" : "light");
            break;
          case "theme.setTheme":
            {
              const [theme] = argsFactory(handler.args, runtimeContext, event);
              applyTheme(theme);
              break;
            }
          case "mode.setDashboardMode":
          case "mode.setDefaultMode":
            applyMode(handler.action === "mode.setDashboardMode" ? "dashboard" : "default");
            break;

          // case "menu.clearMenuTitleCache":
          // case "menu.clearMenuCache":

          // case "analytics.event":

          // case "preview.debug":

          default:
            // eslint-disable-next-line no-console
            console.error("unknown event listener action:", handler.action);
        }
      } else if (isUseProviderHandler(handler)) {
        handleUseProviderAction(event, handler, runtimeContext, runtimeBrick);
      } else if (isCustomHandler(handler)) {
        handleCustomAction(event, handler, runtimeContext, runtimeBrick);
      } else {
        // eslint-disable-next-line no-console
        console.error("unknown event handler:", handler);
      }
    }
  };
}
function handleEventAction(event, method, args, runtimeContext) {
  const computedArgs = argsFactory(args, runtimeContext, event, {
    useEventAsDefault: true
  });
  const [e] = computedArgs;
  if (e instanceof Event) {
    e[method]();
  } else {
    // eslint-disable-next-line no-console
    console.error(`call event.${method}() on non-Event object:`, e);

    // For compatibility, fallback to the original event.
    event[method]();
  }
}
function handleUseProviderAction(event, handler, runtimeContext, runtimeBrick) {
  const method = handler.method !== "saveAs" ? "resolve" : "saveAs";
  brickCallback(event, handler.useProvider, handler, method, runtimeContext, runtimeBrick);
}
function handleCustomAction(event, handler, runtimeContext, runtimeBrick) {
  let targets = [];
  const rawTarget = handler.target;
  const rawTargetRef = handler.targetRef;
  let computedTarget = rawTarget;
  // Allow `target` to be set as evaluable string.
  if (typeof rawTarget === "string" ? isEvaluable(rawTarget) : isPreEvaluated(rawTarget)) {
    computedTarget = computeRealValue(rawTarget, {
      ...runtimeContext,
      event
    });
  }
  if (typeof computedTarget === "string") {
    if (computedTarget === "_self") {
      targets.push(runtimeBrick.element);
    } else if (handler.multiple) {
      targets = Array.from(document.querySelectorAll(computedTarget));
    } else {
      const found = document.querySelector(computedTarget);
      if (found !== null) {
        targets.push(found);
      }
    }
  } else if (computedTarget) {
    if (computedTarget instanceof HTMLElement) {
      targets.push(computedTarget);
    } else {
      // eslint-disable-next-line no-console
      console.error("unexpected target:", computedTarget);
    }
  } else if (rawTargetRef) {
    let computedTargetRef = rawTargetRef;
    // Allow `targetRef` to be set as evaluable string.
    if (typeof rawTargetRef === "string" ? isEvaluable(rawTargetRef) : isPreEvaluated(rawTargetRef)) {
      computedTargetRef = computeRealValue(rawTargetRef, {
        ...runtimeContext,
        event
      });
    }
    const targetRefs = [].concat(computedTargetRef);
    const tplHostElement = getTplHostElement(runtimeContext, "targetRef", `: ${targetRefs.join(", ")}`);
    targets.push(...targetRefs.map(ref => {
      var _tplHostElement$$$get;
      return (_tplHostElement$$$get = tplHostElement.$$getElementByRef) === null || _tplHostElement$$$get === void 0 ? void 0 : _tplHostElement$$$get.call(tplHostElement, ref);
    }).filter(Boolean));
  }
  if (targets.length === 0) {
    // eslint-disable-next-line no-console
    console.error("target not found:", rawTarget || rawTargetRef);
    return;
  }
  if (isExecuteCustomHandler(handler)) {
    targets.forEach(target => {
      brickCallback(event, target, handler, handler.method, runtimeContext, runtimeBrick, {
        useEventAsDefault: true
      });
    });
  } else if (isSetPropsCustomHandler(handler)) {
    setProperties(targets, handler.properties, {
      ...runtimeContext,
      event
    });
  }
}
async function brickCallback(event, targetOrProvider, handler, method, runtimeContext, runtimeBrick, options) {
  const isProvider = isUseProviderHandler(handler);
  const task = async () => {
    var _hooks$flowApi;
    const realTarget = isProvider ? await getProviderBrick(targetOrProvider) : targetOrProvider;
    if (typeof realTarget[method] !== "function") {
      throw new Error(`target <${realTarget.tagName.toLowerCase()}> has no method: ${method}`);
    }
    const isUseProvider = isUseProviderHandler(handler);
    let computedArgs;
    let argsOrContractConf = isUseProvider ? computeArgs(handler.args, runtimeContext, event) : argsFactory(handler.args, runtimeContext, event, options);
    if (isUseProvider && hooks !== null && hooks !== void 0 && (_hooks$flowApi = hooks.flowApi) !== null && _hooks$flowApi !== void 0 && _hooks$flowApi.isFlowApiProvider(handler.useProvider)) {
      var _handler$sse;
      if (!Array.isArray(handler.args) && handler.params) {
        argsOrContractConf = computeRealValue(pick(handler, "params", "options", "filename"), {
          ...runtimeContext,
          event
        });
      }
      computedArgs = await hooks.flowApi.getArgsOfFlowApi(handler.useProvider, argsOrContractConf !== null && argsOrContractConf !== void 0 ? argsOrContractConf : [], method, (_handler$sse = handler.sse) === null || _handler$sse === void 0 ? void 0 : _handler$sse.stream);
    } else {
      computedArgs = argsOrContractConf !== null && argsOrContractConf !== void 0 ? argsOrContractConf : [];
    }
    return realTarget[method](...computedArgs);
  };
  if (!handler.callback) {
    task();
    return;
  }
  const callbackFactory = eventCallbackFactory(handler.callback, runtimeContext, runtimeBrick);
  const pollableCallback = {
    progress: callbackFactory("progress"),
    success: callbackFactory("success"),
    error: callbackFactory("error"),
    finally: callbackFactory("finally")
  };
  if (isProvider) {
    var _handler$sse2, _handler$poll;
    const pollRuntimeContext = {
      ...runtimeContext,
      event
    };
    const sseStream = computeRealValue((_handler$sse2 = handler.sse) === null || _handler$sse2 === void 0 ? void 0 : _handler$sse2.stream, pollRuntimeContext);
    if (sseStream) {
      startSSEStream(task, pollableCallback);
      return;
    }
    const pollEnabled = computeRealValue((_handler$poll = handler.poll) === null || _handler$poll === void 0 ? void 0 : _handler$poll.enabled, pollRuntimeContext);
    if (pollEnabled) {
      startPoll(task, pollableCallback, handler.poll, pollRuntimeContext);
      return;
    }
  }
  try {
    // Try to catch synchronized tasks too.
    const result = await task();
    pollableCallback.success(result);
  } catch (err) {
    pollableCallback.error(err);
  } finally {
    pollableCallback["finally"]();
  }
}
function handleHistoryAction(event, method, args, callback, runtimeContext) {
  let baseArgsLength = 0;
  let hasCallback = false;
  let overrideMethod = method;
  switch (method) {
    case "push":
    case "replace":
    case "pushQuery":
    case "replaceQuery":
    case "pushAnchor":
      baseArgsLength = 2;
      hasCallback = true;
      break;
    case "reload":
      hasCallback = true;
      break;
    case "block":
      baseArgsLength = 1;
      overrideMethod = "setBlockMessage";
      break;
  }
  let computedArgs = [];
  if (baseArgsLength > 0) {
    computedArgs = argsFactory(args, runtimeContext, event, {
      useEventDetailAsDefault: true
    });
    computedArgs.length = baseArgsLength;
  }
  if (hasCallback && callback) {
    const callbackFactory = eventCallbackFactory(callback, runtimeContext, undefined);
    computedArgs.push(blocked => {
      callbackFactory(blocked ? "error" : "success")({
        blocked
      });
      callbackFactory("finally")({
        blocked
      });
    });
  }
  getHistory()[overrideMethod](...computedArgs);
}
function handleWindowOpenAction(event, args, runtimeContext) {
  const [url, target, features] = argsFactory(args, runtimeContext, event);
  window.open(url, target || "_self", features);
}
function handleWindowPostMessageAction(event, args, runtimeContext) {
  const computedArgs = argsFactory(args, runtimeContext, event);
  if (computedArgs.length === 1) {
    // Add default target origin
    computedArgs.push(location.origin);
  }
  window.postMessage(...computedArgs);
}
function handleParentPostMessageAction(event, args, runtimeContext) {
  if (parent === window) {
    throw new Error("parent is the window itself");
  }
  const computedArgs = argsFactory(args, runtimeContext, event);
  parent.postMessage(...computedArgs);
}
function batchUpdate(args, batch, method, store, runtimeContext, event) {
  if (batch) {
    store.updateValues(args, method, arg => {
      return argsFactory(arg, runtimeContext, event)[0];
    });
  } else {
    args.forEach(arg => {
      const {
        name,
        value
      } = argsFactory([arg], runtimeContext, event)[0];
      store.updateValue(name, value, method);
    });
  }
}
function handleContextAction(event, method, args, batch, callback, runtimeContext) {
  const isBatch = Array.isArray(args) && args.every(isObject);
  if (isBatch && (method === "assign" || method === "replace")) {
    batchUpdate(args, batch, method, runtimeContext.ctxStore, runtimeContext, event);
  } else {
    const [name, value] = argsFactory(args, runtimeContext, event);
    runtimeContext.ctxStore.updateValue(name, value, method, callback, runtimeContext);
  }
}
function handleTplStateAction(event, method, args, batch, callback, runtimeContext) {
  const isBatch = Array.isArray(args) && args.every(isObject);
  if (isBatch && method === "update") {
    const tplStateStore = getTplStateStore(runtimeContext, `state.${method}`, `: ${JSON.stringify(args)}`);
    batchUpdate(args, batch, "replace", tplStateStore, runtimeContext, event);
  } else {
    const [name, value] = argsFactory(args, runtimeContext, event);
    const tplStateStore = getTplStateStore(runtimeContext, `state.${method}`, `: ${name}`);
    tplStateStore.updateValue(name, value, method === "update" ? "replace" : method, callback, runtimeContext);
  }
}
function handleFormStateAction(event, args, callback, runtimeContext) {
  const [name, value] = argsFactory(args, runtimeContext, event);
  const formStateStore = getFormStateStore(runtimeContext, "formstate.update", `: ${name}`);
  formStateStore.updateValue(name, value, "replace", callback, runtimeContext);
}
function handleLocationAction(event, method, args, runtimeContext) {
  if (method === "assign") {
    const [url] = argsFactory(args, runtimeContext, event);
    location.assign(url);
  } else {
    location[method]();
  }
}
function handleStorageAction(event, object, method, args, runtimeContext) {
  const storage = object === "localStorage" ? localStorage : sessionStorage;
  const [name, value] = argsFactory(args, runtimeContext, event);
  if (method === "setItem") {
    if (value !== undefined) {
      storage.setItem(name, JSON.stringify(value));
    }
  } else {
    storage.removeItem(name);
  }
}
function handleConsoleAction(event, method, args, runtimeContext) {
  const isProduction = process.env.NODE_ENV === "production";

  // 生产环境下，只有调试模式开启且符合日志级别才输出
  if (isProduction && !debugManager.shouldLog(method)) {
    return;
  }

  // eslint-disable-next-line no-console
  console[method](...argsFactory(args, runtimeContext, event, {
    useEventAsDefault: true
  }));
}
function handleMessageAction(event, method, args, runtimeContext) {
  const computedArgs = argsFactory(args, runtimeContext, event, {
    useEventAsDefault: true
  });
  Notification.show({
    type: method,
    message: computedArgs[0]
  });
}
async function handleMessageDispatcher(event, method, args, runtimeContext, runtimeBrick, callback) {
  const task = () => {
    var _hooks$messageDispatc;
    const computedArgs = argsFactory(args, runtimeContext, event);
    return hooks === null || hooks === void 0 || (_hooks$messageDispatc = hooks.messageDispatcher) === null || _hooks$messageDispatc === void 0 ? void 0 : _hooks$messageDispatc[method](...computedArgs);
  };
  if (!callback) {
    task();
    return;
  }
  const callbackFactory = eventCallbackFactory(callback, runtimeContext, runtimeBrick);
  try {
    const result = await task();
    callbackFactory("success")(result);
  } catch (error) {
    callbackFactory("error")(error);
  } finally {
    callbackFactory("finally")();
  }
}
export function eventCallbackFactory(callback, runtimeContext, runtimeBrick) {
  return function callbackFactory(type) {
    return function (result) {
      const handlers = callback === null || callback === void 0 ? void 0 : callback[type];
      if (handlers) {
        try {
          const event = new CustomEvent(`callback.${type}`, {
            detail: result
          });
          listenerFactory(handlers, runtimeContext, runtimeBrick)(event);
        } catch (err) {
          // Do not throw errors in `callback.success` or `callback.progress`,
          // to avoid the following triggering of `callback.error`.
          // eslint-disable-next-line
          console.error(err);
        }
      } else if (type === "error") {
        // eslint-disable-next-line
        console.error("Unhandled callback error:", result);
      }
    };
  };
}
function argsFactory(args, runtimeContext, event) {
  let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return Array.isArray(args) ? computeRealValue(args, {
    ...runtimeContext,
    event
  }) : options.useEventAsDefault ? [event] : options.useEventDetailAsDefault ? [event.detail] : [];
}
function computeArgs(args, runtimeContext, event) {
  if (!Array.isArray(args)) {
    return args;
  }
  return computeRealValue(args, {
    ...runtimeContext,
    event
  });
}
//# sourceMappingURL=bindListeners.js.map